/** Map i18n language codes to BCP-47 locale tags used by the Intl APIs. */
const LOCALE_MAP: Record<string, string> = {
  en: "en-US",
  ja: "ja-JP",
};

const resolveLocale = (lang?: string): string =>
  (lang && LOCALE_MAP[lang]) || LOCALE_MAP["en"];

export const formatDate = (dateStr: string, lang?: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const locale = resolveLocale(lang);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (dateStr: string, lang?: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const locale = resolveLocale(lang);
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDateForAPI = (date: Date | string | null): string | null => {
  if (!date) return null;
  if (date instanceof Date) return date.toISOString().split("T")[0];
  return date || null;
};

/**
 * Formats a Stream `last_active` timestamp into a compact relative string.
 * Returns empty string if the timestamp is missing.
 *
 * Examples: "just now" · "5 min ago" · "3h ago" · "Mon" · "Mar 25"
 */
export const formatLastSeen = (lastActive: string | undefined | null): string => {
  if (!lastActive) return "";
  const date = new Date(lastActive);
  if (isNaN(date.getTime())) return "";

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7)
    return date.toLocaleDateString("en-US", { weekday: "short" });
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
};

/**
 * Returns true if the string contains characters outside the Latin script
 * (Basic Latin through Latin Extended Additional, U+0000–U+024F).
 */
const isNonRoman = (str: string): boolean => /[^\u0000-\u024F]/.test(str ?? "");

/**
 * Formats a display name as: LastName, FirstName MiddleName
 *
 * Constraints:
 *  - No firstName → LastName only (no comma)
 *  - Non-Roman characters in any name part → no comma (space-separated)
 */
export const formatDisplayName = (
  lastName?: string | null,
  firstName?: string | null,
  middleName?: string | null,
): string => {
  const last = lastName?.trim() ?? "";
  const first = firstName?.trim() ?? "";
  const mid = middleName?.trim() ?? "";

  if (!last) return [first, mid].filter(Boolean).join(" ");

  const givenName = [first, mid].filter(Boolean).join(" ");

  if (isNonRoman(last) || isNonRoman(first) || isNonRoman(mid)) {
    return [last, givenName].filter(Boolean).join(" ");
  }

  if (!first) return last;

  return `${last}, ${givenName}`;
};
